import { mapLegacyFields } from './field-mapper';
import type { TrackEvent } from '../../core';
import { MaskingMiddleware } from '../../core/masking';
import { compileParameters } from './parameter-config';
import type { LegacyParameters } from './parameter-types';

/**
 * 对 URL 键或值进行标准 urlencoded 编码
 * 将空格编码为 +，保持与 application/x-www-form-urlencoded 标准及历史服务端行为严格一致
 */
function encodeQueryComponent(val: unknown): string {
  if (val === undefined || val === null) return '';
  const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
  return encodeURIComponent(toScalarString(str))
    .replace(/[!'()~]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)
    .replace(/%20/g, '+');
}

/** 与 URLSearchParams 一致：将孤立的 UTF-16 代理项替换为 U+FFFD。 */
function toScalarString(value: string): string {
  let result = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xD800 && code <= 0xDBFF) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xDC00 && next <= 0xDFFF) {
        result += value[index] + value[index + 1];
        index += 1;
      } else {
        result += '\uFFFD';
      }
    } else {
      result += code >= 0xDC00 && code <= 0xDFFF ? '\uFFFD' : value[index];
    }
  }
  return result;
}

/**
 * 格式化标准事件为海典旧版 behavior 协议所需的 urlencoded 载荷
 * 纯 JS 实现，无任何 DOM/Web API（如 URLSearchParams）依赖，兼容微信/支付宝小程序、Taro、Uni 等所有运行时
 */
export function formatLegacyPayload(event: TrackEvent, parameters?: LegacyParameters): string {
  return createPayloadFormatter(parameters)(event);
}

/** @internal 插件只创建一次格式化器，使队列及重试使用固定规则。 */
export function createPayloadFormatter(parameters?: LegacyParameters): (event: TrackEvent) => string {
  const config = compileParameters(parameters);
  const masking = new MaskingMiddleware();
  return (event) => {
    try {
      const mapped = mapLegacyFields(event, config);
      // 按最终目标名脱敏，覆盖别名及默认值；旧模式保持历史行为。
      const safeFields = config.legacy ? mapped : masking.mask(mapped);
      // merCodeEmpty 是旧请求拦截器的内部标记，不属于 HTTP 入参。
      const fields = { ...safeFields, _randomTemp: Math.random().toString() };
      return Object.entries(fields)
        .map(([key, value]) => `${encodeQueryComponent(key)}=${encodeQueryComponent(value)}`)
        .join('&');
    } catch {
      // 空串表示不可发送，绝不回退成未经校验的原始载荷。
      return '';
    }
  };
}
